import { useEffect } from 'react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

export default function ManifestoPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? null;

    document.title = 'The Manifesto - Music Is the Gift | fans.audio';
    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Music is the gift. The relationship is the value. Read the fans.audio manifesto for independent artists, meaningful fan relationships and music worth collecting.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta && previousDescription !== null) {
        descriptionMeta.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg tracking-tight">fans<span className="text-electric-cyan">.audio</span></span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="/for-fans" className="text-sm text-text-secondary hover:text-electric-cyan transition-colors">For Fans</a>
            <a href="/for-artists" className="text-sm text-text-secondary hover:text-electric-cyan transition-colors">For Artists</a>
            <a href="/manifesto" className="text-sm text-electric-cyan transition-colors">Manifesto</a>
          </div>
          <a href="/request-access" className="btn-primary text-sm !px-5 !py-2.5 inline-flex items-center justify-center">Apply to Join</a>
        </div>
      </nav>

      <main className="pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <motion.article initial="hidden" animate="visible" variants={staggerContainer} className="max-w-6xl mx-auto">
          <motion.section variants={fadeInUp} className="max-w-4xl mx-auto text-center mb-20 sm:mb-24">
            <p className="terminal-label text-xs sm:text-sm mb-6">THE FANS.AUDIO MANIFESTO</p>
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-6">
              Music is the gift.
              <br />
              The <span className="text-gradient-cyan">relationship</span> is the value.
            </h1>
            <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              A different way to think about music, artists and the people who care about them.
            </p>
          </motion.section>

          <motion.section variants={fadeInUp} className="max-w-3xl mx-auto mb-20 sm:mb-24">
            <div className="space-y-6 text-[1.08rem] sm:text-xl leading-relaxed text-text-secondary">
              <p>
                The internet made it possible for anyone to release music. It also gave artists more ways than ever to share their work, reach listeners and build an audience.
              </p>
              <p>
                But somewhere along the way, the relationship between artists and fans became fragmented across platforms, feeds and services.
              </p>
              <p>We believe there's room for something different.</p>
              <p>
                Not by replacing the places where people discover music, but by giving artists somewhere to bring those listeners together.
              </p>
              <p className="text-white">
                A home for their music. A place for their fans. A relationship built around the work itself.
              </p>
            </div>
          </motion.section>

          <motion.hr variants={fadeInUp} className="border-white/10 mb-20 sm:mb-24" />

          <motion.section variants={fadeInUp} className="max-w-4xl mx-auto mb-20 sm:mb-24">
            <p className="terminal-label text-xs sm:text-sm mb-4">01 / THE RELATIONSHIP</p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] mb-6">A stream is not a fan.</h2>
            <div className="max-w-2xl space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary">
              <p>A follower is not necessarily a connection.</p>
              <p>Algorithms change. Platforms come and go.</p>
              <p>
                But the relationship between an artist and someone who genuinely cares about their music can last a lifetime.
              </p>
              <p>
                fans.audio exists to help artists build direct, lasting relationships with the people who choose to support them.
              </p>
            </div>
            <p className="mt-8 text-white text-2xl sm:text-3xl font-bold leading-tight">
              The artist owns the relationship. The fan chooses it.
            </p>
          </motion.section>

          <motion.hr variants={fadeInUp} className="border-white/10 mb-20 sm:mb-24" />

          <motion.section variants={fadeInUp} className="max-w-5xl mx-auto mb-20 sm:mb-24 text-center">
            <p className="terminal-label text-xs sm:text-sm mb-4">02 / THE MUSIC</p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] mb-8">Music is meant to be collected.</h2>
            <div className="max-w-3xl mx-auto space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary text-left">
              <p>
                Every release should have the opportunity to become something more than another stream.
              </p>
              <p>
                Something a fan can discover, appreciate and keep as part of their personal collection.
              </p>
              <p>
                That's why fans.audio introduces Pressings - digital editions of music that artists create and fans can choose to collect.
              </p>
              <p>They're not disposable downloads or another entry in an endless feed.</p>
              <p>They're part of an artist's catalogue.</p>
              <p>And part of a fan's story.</p>
            </div>
            <p className="mt-10 text-white text-2xl sm:text-4xl font-bold leading-tight">Music deserves more than a moment.</p>
          </motion.section>

          <motion.hr variants={fadeInUp} className="border-white/10 mb-20 sm:mb-24" />

          <motion.section variants={fadeInUp} className="max-w-5xl mx-auto mb-20 sm:mb-24">
            <p className="terminal-label text-xs sm:text-sm mb-4">03 / COMMUNICATION</p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] mb-6">Transmissions, not newsletters.</h2>
            <div className="max-w-3xl space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary mb-8">
              <p>Artists shouldn't have to become marketers to stay connected with their fans.</p>
              <p>Sometimes there's something worth sharing.</p>
            </div>
            <div className="max-w-4xl border-l border-electric-cyan/50 pl-6 sm:pl-8 py-1 mb-8">
              <p className="text-white text-xl sm:text-2xl leading-relaxed">
                A new song. A demo. A thought from the studio. A live date. A lyric.
              </p>
            </div>
            <div className="max-w-3xl space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary">
              <p>fans.audio calls these Transmissions.</p>
              <p>
                A way for artists to communicate directly with the people who've chosen to hear from them.
              </p>
              <p>Not because an algorithm decided they should see it.</p>
              <p>Because the fan asked to be there.</p>
            </div>
            <p className="mt-8 text-white text-2xl sm:text-3xl font-bold leading-tight">
              When you've got something to say, your fans should be able to hear it.
            </p>
          </motion.section>

          <motion.hr variants={fadeInUp} className="border-white/10 mb-20 sm:mb-24" />

          <motion.section variants={fadeInUp} className="max-w-5xl mx-auto mb-20 sm:mb-24">
            <p className="terminal-label text-xs sm:text-sm mb-4">04 / COMMUNITY</p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-5">
                <h2 className="text-4xl sm:text-5xl font-bold leading-[1.05]">Great music travels through people.</h2>
              </div>
              <div className="lg:col-span-7 space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary">
                <p>
                  Artists have always helped other artists find an audience. Through recommendations, collaborations, shared stages and word of mouth.
                </p>
                <p>We believe a platform should encourage those connections.</p>
                <p>Artists introducing fans to other artists.</p>
                <p>Listeners discovering music through people they trust.</p>
                <p>Audiences growing together.</p>
              </div>
            </div>
            <p className="mt-8 text-white text-2xl sm:text-3xl font-bold leading-tight text-right">Community creates culture.</p>
          </motion.section>

          <motion.hr variants={fadeInUp} className="border-white/10 mb-20 sm:mb-24" />

          <motion.section variants={fadeInUp} className="max-w-4xl mx-auto mb-20 sm:mb-24">
            <p className="terminal-label text-xs sm:text-sm mb-4">05 / TECHNOLOGY</p>
            <h2 className="text-4xl sm:text-5xl font-bold leading-[1.05] mb-6">Technology should serve the music.</h2>
            <div className="space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary max-w-3xl">
              <p>Technology should make creation easier.</p>
              <p>Sharing simpler.</p>
              <p>Relationships stronger.</p>
              <p>
                Artists shouldn't need an ever-growing collection of services just to stay connected with the people who appreciate their work.
              </p>
              <p>
                The tools should work quietly in the background, leaving artists free to focus on what matters.
              </p>
            </div>
            <p className="mt-10 text-white text-3xl sm:text-4xl font-bold leading-tight">
              The software isn't the destination. The music is.
            </p>
          </motion.section>

          <motion.hr variants={fadeInUp} className="border-white/10 mb-20 sm:mb-24" />

          <motion.section variants={fadeInUp} className="max-w-5xl mx-auto mb-20 sm:mb-24 text-center">
            <p className="terminal-label text-xs sm:text-sm mb-4">06 / THE FUTURE</p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] mb-8">Build something that lasts.</h2>
            <div className="max-w-3xl mx-auto space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary text-left">
              <p>We're not building another social network.</p>
              <p>We're building a home for independent music.</p>
              <p>
                A place where artists can bring together their catalogue, their releases and the people who care about them.
              </p>
              <p>Where a listener can become a fan.</p>
              <p>Where a release can become part of someone's collection.</p>
              <p>And where the relationship between artist and fan can grow over time.</p>
            </div>
            <p className="mt-10 text-white text-3xl sm:text-4xl font-bold leading-tight">
              Your music isn't just content. It's your life's work.
            </p>
            <p className="mt-4 text-white text-2xl sm:text-3xl font-semibold">And life's work deserves a home.</p>
          </motion.section>

          <motion.hr variants={fadeInUp} className="border-white/10 mb-20 sm:mb-24" />

          <motion.section variants={fadeInUp} className="max-w-4xl mx-auto text-center mb-16 sm:mb-20">
            <p className="terminal-label text-xs sm:text-sm mb-4">NEXT / BUILD TOGETHER</p>
            <h2 className="text-4xl sm:text-5xl font-bold leading-[1.1] mb-6">Help shape what comes next.</h2>
            <div className="space-y-5 text-[1.05rem] sm:text-xl leading-relaxed text-text-secondary max-w-3xl mx-auto">
              <p>
                fans.audio is growing with independent artists who believe music deserves a home and fan relationships deserve to last.
              </p>
              <p>We're inviting artists to help build that future.</p>
            </div>
            <p className="mt-10 text-white text-2xl sm:text-3xl font-bold leading-tight">
              One artist. One release. One relationship at a time.
            </p>
            <div className="mt-10">
              <a href="/request-access" className="btn-primary text-base inline-flex items-center">
                Apply to Join
              </a>
            </div>
          </motion.section>
        </motion.article>
      </main>

      <footer className="border-t border-white/5 px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-6 h-6 object-contain" />
              <span className="font-mono font-bold text-sm">fans<span className="text-electric-cyan">.audio</span></span>
            </div>
            <div className="flex items-center gap-6">
              <a href="/for-fans" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">For Fans</a>
              <a href="/for-artists" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">For Artists</a>
              <a href="/manifesto" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">Manifesto</a>
              <a href="/terms-of-service" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">Terms of Service</a>
              <a href="/privacy-policy" className="text-xs text-text-muted hover:text-electric-cyan transition-colors">Privacy Policy</a>
            </div>
          </div>
          <div className="mt-8 text-center space-y-2">
            <p className="text-xs text-text-muted">fans.audio is a trading style of Boogie Microphone Limited.</p>
            <p className="font-mono text-xs text-text-muted">Contact: admin@bmic.music</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
